import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { TRADE_DEMOS } from "@/data/tradeDemos";
import SiteMockup from "@/components/SiteMockup";

const categories = ["All", "Contracting", "Roofing & HVAC", "Exterior Services", "Luxury Renovation"];

const TIER_TABS = [
  { key: "starter", label: "Starter" },
  { key: "growth", label: "Growth" },
  { key: "premium", label: "Premium" },
];

const projects = [
  {
    id: "summit",
    name: "Summit General Contracting",
    category: "Contracting",
    metric: "+210% inbound leads",
    image: "https://images.unsplash.com/photo-1587582423116-ec07293f0395?crop=entropy&cs=srgb&fm=jpg&q=85",
    desc: "Full rebrand and 8-page site for a San Diego GC. Project gallery, crew bios, and a quote funnel that filters tire-kickers before they call.",
    demo: TRADE_DEMOS["general-contracting"],
  },
  {
    id: "apex",
    name: "Apex Roofing & Air",
    category: "Roofing & HVAC",
    metric: "3.1x quote requests",
    image: "https://images.unsplash.com/photo-1754620906703-496ec2451744?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHw0fHxyb29mZXIlMjB3b3JraW5nJTIwb24lMjByb29mJTIwSFZBQyUyMGluc3RhbGxhdGlvbnxlbnwwfHx8fDE3OTM3MzQxNDZ8MA&ixlib=rb-4.1.0&q=85",
    desc: "Storm-season landing system with emergency-repair CTAs, financing calculator, and service-area pages for 12 zip codes.",
    demo: TRADE_DEMOS["roofing-hvac"],
  },
  {
    id: "clearline",
    name: "Clearline Pressure Washing",
    category: "Exterior Services",
    metric: "+180% booked jobs",
    image: "https://images.unsplash.com/photo-1677956787377-a0f32c0974af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxwcmVzc3VyZSUyMHdhc2hpbmclMjBzcHJheSUyMGNsZWFuaW5nJTIwd2F0ZXIlMjB3b3JrZXJ8ZW58MHx8fHwxNzkzNzM0MTUzfDA&ixlib=rb-4.1.0&q=85",
    desc: "Before/after gallery engine and instant-quote form. Customers pick surfaces, upload photos, and book a slot — no phone tag.",
    demo: TRADE_DEMOS["pressure-washing"],
  },
  {
    id: "meridian",
    name: "Meridian Luxury Renovation",
    category: "Luxury Renovation",
    metric: "+95% avg. job value",
    image: "https://images.unsplash.com/photo-1593313637552-29c2c0dacd35?crop=entropy&cs=srgb&fm=jpg&q=85",
    desc: "Editorial-style portfolio for high-end remodels. Full-bleed project stories that attract six-figure renovation clients.",
    demo: {
      slug: "general-contracting",
      trade: "Luxury Renovation",
      demoBusiness: "Meridian Luxury Renovation",
      accent: "#C9A96E",
      services: ["Kitchen Remodels", "Whole-Home Renovation", "Design-Build"],
    },
  },
];

function Card({ p, onOpen }) {
  const ref = useRef(null);
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    ref.current.style.setProperty("--mouse-x", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--mouse-y", `${e.clientY - r.top}px`);
  };
  return (
    <motion.button
      ref={ref}
      onMouseMove={onMove}
      onClick={() => onOpen(p)}
      data-testid={`portfolio-card-${p.id}`}
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -6 }}
      className="spotlight-card group text-left rounded-2xl border gold-border-subtle bg-surface/60 overflow-hidden hover:border-gold/40 transition-colors duration-500"
    >
      <div className="relative h-56 sm:h-64 overflow-hidden">
        <img
          src={p.image}
          alt={p.name}
          className="w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent" />
        <div className="absolute top-4 right-4 px-3 py-1.5 rounded-full bg-obsidian/70 backdrop-blur border border-gold/30 font-mono text-[10px] uppercase tracking-[0.15em] text-gold">
          {p.metric}
        </div>
      </div>
      <div className="p-7">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">{p.category}</p>
        <div className="flex items-center justify-between gap-4">
          <h3 className="font-serif text-2xl text-[#F8F6F0]">{p.name}</h3>
          <ArrowUpRight size={20} className="text-gold opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300 shrink-0" />
        </div>
      </div>
    </motion.button>
  );
}

export default function Portfolio() {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const [tier, setTier] = useState("growth");

  const open = (p) => {
    setActive(p);
    setTier("growth");
  };

  const shown = filter === "All" ? projects : projects.filter((p) => p.category === filter);

  return (
    <section id="work" data-testid="agency-portfolio-section" className="py-28 lg:py-36 bg-surface/30 border-y gold-border-subtle">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-12 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8"
        >
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-gold/80 mb-4">The Portfolio</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-[#F8F6F0] tracking-tight max-w-2xl">
              Find your trade. <span className="gold-text italic">See your future.</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="portfolio-filters">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setFilter(c)}
                className={`px-4 py-2 rounded-full font-mono text-[11px] uppercase tracking-[0.15em] transition-all duration-300 ${
                  filter === c
                    ? "bg-gold text-obsidian font-medium"
                    : "border border-zinc-700 text-zinc-400 hover:border-gold/50 hover:text-gold"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <AnimatePresence mode="popLayout">
            {shown.map((p) => (
              <Card key={p.id} p={p} onOpen={open} />
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8 bg-obsidian/85 backdrop-blur-md"
            onClick={() => setActive(null)}
            data-testid="portfolio-modal-overlay"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 24 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              data-testid="portfolio-modal"
              className="trace-border relative w-full max-w-4xl rounded-3xl border gold-border-subtle bg-surface overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b gold-border-subtle">
                <div className="flex flex-wrap items-center gap-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{active.name} · example site</span>
                  <div className="flex gap-2" data-testid="modal-tier-tabs">
                    {TIER_TABS.map((t) => (
                      <button
                        key={t.key}
                        data-testid={`modal-tier-${t.key}`}
                        onClick={() => setTier(t.key)}
                        className={`px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-wider transition-colors ${
                          tier === t.key ? "bg-gold text-obsidian" : "text-zinc-400 hover:text-gold border border-zinc-800"
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button data-testid="portfolio-modal-close" onClick={() => setActive(null)} className="text-zinc-500 hover:text-gold transition-colors">
                  <X size={22} />
                </button>
              </div>
              <div className="p-6 sm:p-8 max-h-[72vh] overflow-y-auto">
                <SiteMockup demo={active.demo} tier={tier} />
                <div className="mt-7 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
                  <div>
                    <h3 className="font-serif text-3xl text-[#F8F6F0]">{active.name}</h3>
                    <p className="text-sm text-zinc-400 mt-2 max-w-xl leading-relaxed">{active.desc}</p>
                  </div>
                  <span className="shrink-0 px-4 py-2 rounded-full border border-gold/40 font-mono text-xs uppercase tracking-[0.15em] text-gold">
                    {active.metric}
                  </span>
                </div>
                <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
                    Demonstration concept — fictional business shown for illustrative purposes.
                  </p>
                  <Link
                    to={`/trades/${active.demo.slug}`}
                    data-testid="portfolio-modal-trade-link"
                    className="group flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-gold hover:text-gold-light transition-colors"
                  >
                    Full {active.demo.trade} showcase <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
